<script setup>
import { onMounted, onUnmounted, ref, watch } from 'vue';

const props = defineProps({
    modelValue: { type: [String, Blob], default: '' },
    inputId: { type: String, default: '' },
    filename: { type: String, default: '' },
    label: { type: String, default: '' },
    required: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    marginTop: { type: String, default: '0' },
    marginBottom: { type: String, default: '0' },
    marginLeft: { type: String, default: '0' },
    marginRight: { type: String, default: '0' },
    labelRightmargin: { type: String, default: '2rem' },
    visible: { type: Boolean, default: true },
    height: { type: String, default: '3rem' },
    width: { type: String, default: '100%' },
    readonly: { type: Boolean, default: false },
    errmsg: { type: String, default: '' }
});

const emit = defineEmits(['update:modelValue']);

const canvasRef = ref(null);
const isDrawing = ref(false);
const ValidationMsg = ref('');
let ctx;
let lastX = 0; // 이전 X 좌표 저장
let lastY = 0; // 이전 Y 좌표 저장

// 캔버스 크기 조절 함수
const handleResize = () => {
    // if (canvasRef.value) {
    //     canvasRef.value.width = canvasRef.value.offsetWidth;
    //     canvasRef.value.height = canvasRef.value.offsetHeight;
    // }
    if (!canvasRef.value) return;
    const canvas = canvasRef.value;

    // 기존 내용 백업
    const prevImage = ctx.getImageData(0, 0, canvas.width, canvas.height);

    // 새 사이즈 적용
    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;

    // 선 스타일 다시 설정 (리셋됨)
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#000';

    // 백업 이미지 다시 그림
    ctx.putImageData(prevImage, 0, 0);
};

onMounted(() => {
    const canvas = canvasRef.value;
    ctx = canvas.getContext('2d', { willReadFrequently: true });

    // 캔버스 크기를 실제 렌더링된 크기에 맞춤 (반응형)
    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;

    ctx.lineWidth = 2; // 선 굵기
    ctx.lineCap = 'round'; // 선 끝을 둥글게
    ctx.lineJoin = 'round'; // 선 연결 부위를 둥글게
    ctx.strokeStyle = '#000';

    canvas.addEventListener('touchstart', startDrawing, { passive: false });
    canvas.addEventListener('touchmove', draw, { passive: false });
    canvas.addEventListener('touchend', stopDrawing);

    // 창 크기 변경 이벤트 리스너 추가
    window.addEventListener('resize', handleResize);
});

onUnmounted(() => {
    const canvas = canvasRef.value;
    if (canvas) {
        canvas.removeEventListener('touchstart', startDrawing);
        canvas.removeEventListener('touchmove', draw);
        canvas.removeEventListener('touchend', stopDrawing);
    }
    window.removeEventListener('resize', handleResize);
});

watch(
    () => props.errmsg,
    (newVal) => {
        ValidationMsg.value = newVal || '';
    }
);

const startDrawing = (e) => {
    if (props.readonly || props.disabled) return;
    e.preventDefault();
    isDrawing.value = true;

    const rect = canvasRef.value.getBoundingClientRect();
    // 터치 이벤트와 마우스 이벤트 모두 처리
    const clientX = e.clientX || (e.touches && e.touches[0].clientX);
    const clientY = e.clientY || (e.touches && e.touches[0].clientY);

    [lastX, lastY] = [clientX - rect.left, clientY - rect.top];
    ctx.beginPath(); // 새로운 경로 시작
    //   ctx.moveTo(lastX, lastY); // 시작점으로 이동 (불필요: draw에서 처리)
};

const draw = (e) => {
    if (!isDrawing.value || props.readonly || props.disabled) return;
    e.preventDefault();

    const rect = canvasRef.value.getBoundingClientRect();
    const clientX = e.clientX || (e.touches && e.touches[0].clientX);
    const clientY = e.clientY || (e.touches && e.touches[0].clientY);
    const currentX = clientX - rect.left;
    const currentY = clientY - rect.top;

    ctx.beginPath(); // 매번 새로운 path 시작 (잔상 제거)
    ctx.moveTo(lastX, lastY); // 이전 위치에서
    ctx.lineTo(currentX, currentY); // 현재 위치까지 선 그리기
    ctx.stroke();

    [lastX, lastY] = [currentX, currentY]; // 이전 위치 업데이트
};

const stopDrawing = () => {
    isDrawing.value = false;
    emitSignature();
    // ctx.beginPath(); //  stopDrawing에서는 beginPath() 불필요.
};

const clearSignature = () => {
    if (props.readonly || props.disabled) return;
    ctx.clearRect(0, 0, canvasRef.value.width, canvasRef.value.height);
    emitSignature();
};

const getSignatureBlob = async () => {
    return new Promise((resolve) => {
        canvasRef.value.toBlob((blob) => {
            resolve(blob);
        }, 'image/png');
    });
};

const emitSignature = async () => {
    const blob = await getSignatureBlob();
    emit('update:modelValue', blob);

    // const signatureData = canvasRef.value.toDataURL();
    // emit('update:modelValue', signatureData);
};

const isSignEmpty = () => {
    const canvas = canvasRef.value;
    const pixels = ctx.getImageData(0, 0, canvas.width, canvas.height).data;

    // 모든 픽셀의 알파 값이 0인지 체크 (완전히 비어 있으면 true)
    for (let i = 3; i < pixels.length; i += 4) {
        if (pixels[i] !== 0) return false;
    }
    return true;
};

defineExpose({
    getSignatureBlob,
    isSignEmpty
});
</script>

<template>
    <div
        class="signature-component"
        :style="{
            marginTop: marginTop,
            marginBottom: marginBottom,
            marginLeft: marginLeft,
            marginRight: marginRight
        }"
    >
        <!-- <label class="label">
            {{ label }}
            <span v-if="required" class="required-asterisk">*</span>
        </label> -->
        <span v-if="ValidationMsg" class="validation-message">{{ ValidationMsg }}</span>
        <div class="canvas-container" :style="{ height: height, width: width }">
            <canvas ref="canvasRef" :style="{ height: '100%', width: '100%' }" @mousedown="startDrawing" @mousemove="draw" @mouseup="stopDrawing" @mouseout="stopDrawing"></canvas>
            <button :disabled="props.readonly || props.disabled" @click="clearSignature" class="clear-button">
                <i class="pi pi-refresh"></i>
            </button>
        </div>
    </div>
</template>

<style scoped>
/* (스타일은 이전과 동일) */
.signature-component {
    display: flex;
    flex-direction: column;
}

.label {
    font-family: 'SCoreDream600';
    display: flex;
    align-items: center;
    margin-bottom: 0.25rem;
    margin-left: 0.25rem;
    height: 1.3rem;
}

.required-asterisk {
    color: #f87171;
    margin-left: 0.2rem;
    position: relative;
    top: -0.2rem;
}

.validation-message {
    color: #f87171;
    font-size: 0.75rem;
    margin-left: 0.5rem;
}

.canvas-container {
    position: relative;
    border: 0.1rem solid #d1d5db;
    border-radius: 0.5rem;
    overflow: hidden;
}

.clear-button {
    position: absolute;
    top: 0.3rem;
    right: 0.3rem;
    padding: 0.35rem;
    background-color: rgba(229, 231, 235, 0.48);
    border: none;
    border-radius: 0.25rem;
    cursor: pointer;
    font-size: 0.75rem;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
}

.clear-button:hover {
    background-color: rgba(209, 213, 219, 0.8);
}

.clear-button:disabled {
    cursor: not-allowed;
    opacity: 0.7;
}

.clear-button i {
    font-size: 0.875rem;
}
</style>
